import {Subscription} from "rxjs";
import {isPlatformBrowser, isPlatformServer} from "@angular/common";
import {Component, inject, OnDestroy, OnInit, PLATFORM_ID} from '@angular/core';

import PhotoSwipe from "photoswipe";
import PhotoSwipeLightbox from "photoswipe/lightbox";

import {ImageViewerItem} from "@numizmat/services-models/common";
import {AutoScrollService, ImageViewerService} from "@numizmat/services/common";

@Component({
    selector: 'nz-image-viewer',
    imports: [],
    templateUrl: './image-viewer.component.html',
    styleUrl: './image-viewer.component.scss'
})
export class ImageViewerComponent implements OnInit, OnDestroy {
  private platformId: Object = inject(PLATFORM_ID);
  private autoScrollService: AutoScrollService = inject(AutoScrollService);
  private imageViewerService: ImageViewerService = inject(ImageViewerService);

  private isClosedByPopState: boolean = false;
  private subs: Subscription = new Subscription();
  private lightbox: PhotoSwipeLightbox | undefined = undefined;

  protected isLoading: boolean = false;
  protected images: ImageViewerItem[] = [];

  public ngOnInit(): void {
    if (isPlatformServer(this.platformId)) {
      return;
    }
    const subscription: Subscription = this.imageViewerService.getImages().subscribe({
      next: (urls: string[]) => {
        if (!!urls && urls.length > 0) {
          this.isLoading = true;
          this.isClosedByPopState = false;
          this.autoScrollService.suppressNextScroll = true;
          history.pushState(null, '', location.href);
          window.addEventListener('popstate', this.popStateHandler);
          this.preloadImages(urls).then(loadedImages => {
            this.images = loadedImages;
            this.initLightbox();
            const firstImage = new Image();
            firstImage.src = this.images[0].url;
            const openLightbox = () => {
              this.lightbox?.loadAndOpen(0);
              this.isLoading = false;
            };
            if (firstImage.complete) {
              openLightbox();
            } else {
              firstImage.onload = openLightbox;
            }
          });
        }
      }
    });
    this.subs.add(subscription);
  }

  public ngOnDestroy(): void {
    this.subs.unsubscribe();
    if (isPlatformBrowser(this.platformId)) {
      this.lightbox?.destroy();
      window.removeEventListener('popstate', this.popStateHandler);
    }
  }

  private preloadImages(urls: string[]): Promise<ImageViewerItem[]> {
    return Promise.all(
      urls.map((url) => {
        return new Promise<ImageViewerItem>((resolve) => {
          const img = new Image();
          img.src = url;
          const done = () =>
            resolve({
              url,
              width: img.naturalWidth || 0,
              height: img.naturalHeight || 0
            });
          img.onload = done;
          img.onerror = done;
          setTimeout(done, 5000);
        });
      })
    );
  }

  private initLightbox(): void {
    this.lightbox = new PhotoSwipeLightbox({
      pswpModule: PhotoSwipe,
      dataSource: this.images.map((img) => ({
        src: img.url,
        width: img.width,
        height: img.height
      })),
      showHideAnimationType: 'none'
    });

    this.lightbox.on('close', () => {
      this.autoScrollService.suppressNextScroll = true;
      window.removeEventListener('popstate', this.popStateHandler);
      this.images = [];
      if (!this.isClosedByPopState) {
        history.back();
      }
    });

    this.lightbox.init();
  }

  private popStateHandler = () => {
    if (!!this.lightbox) {
      this.autoScrollService.suppressNextScroll = false;
      this.isClosedByPopState = true;
      this.lightbox.pswp?.close();
    }
  }
}
